import { useState, useEffect, useRef } from "react";

/**
 * Extract affiliate code from URL format (e.g., "https://advertise.delivait.com/STUDIO137" -> "STUDIO137")
 * @param {string} input - The input string (can be URL or code)
 * @returns {string} - The extracted affiliate code
 */
const extractAffiliateCode = (input) => {
  if (!input || typeof input !== 'string') return '';
  
  const trimmed = input.trim();
  
  // Check if it's a URL format
  const urlMatch = trimmed.match(/advertise\.delivait\.com\/([^\/\s]+)/i);
  if (urlMatch) {
    return urlMatch[1].toUpperCase();
  }
  
  // If it's just a code, return it uppercase
  return trimmed.toUpperCase();
};

/**
 * Standalone function to verify affiliate code from agents
 * @param {string} code - The affiliate code to verify
 * @returns {Promise<{isValid: boolean, data?: any, error?: string}>}
 */
export const verifyAffiliateCode = async (code) => {
  const extractedCode = extractAffiliateCode(code);
  
  if (!extractedCode || extractedCode.length <= 4) {
    return { isValid: false, error: "Code must be longer than 4 characters" };
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URI}/v3/agents/verify-affiliate-code`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ affiliate_code: extractedCode }),
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        isValid: false,
        error: errorData.message || "Failed to verify affiliate code",
      };
    }
    
    const data = await response.json();
    console.log("Affiliate code verification result:", data);

    if (data.status === "success" && data.data?.agent) {
      return {
        isValid: true,
        data: data,
      };
    }

    return {
      isValid: false,
      data: data,
      error: data.message || "Invalid affiliate code",
    };
  } catch (error) {
    console.error("Error verifying affiliate code:", error);
    return {
      isValid: false,
      error: "Failed to verify affiliate code",
    };
  }
};

export const useAffiliateCode = () => {
  const storedCode = localStorage.getItem("deliva_affiliate_code") || "";
  const [affiliateCode, setAffiliateCode] = useState(storedCode);
  // Trust code from localStorage — it was verified before being saved
  const [affiliateCodeValid, setAffiliateCodeValid] = useState(!!storedCode);
  const [affiliateCodeLocked, setAffiliateCodeLocked] = useState(!!storedCode);
  const [affiliateCodeError, setAffiliateCodeError] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const userChangedCode = useRef(false);

  // Only verify when the user explicitly types a new code, not on mount from localStorage
  useEffect(() => {
    if (!userChangedCode.current) {
      return;
    }

    const extractedCode = extractAffiliateCode(affiliateCode);
    
    if (!extractedCode || extractedCode.length <= 4) {
      setAffiliateCodeValid(false);
      setAffiliateCodeError(null);
      if (!affiliateCode || affiliateCode.trim() === '') {
        localStorage.removeItem("deliva_affiliate_code");
      }
      return;
    }

    const timeoutId = setTimeout(() => {
      handleVerification(extractedCode);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [affiliateCode]);

  const handleVerification = async (code) => {
    const extractedCode = extractAffiliateCode(code);
    
    if (!extractedCode || extractedCode.length <= 4) {
      setAffiliateCodeValid(false);
      setAffiliateCodeError(null);
      return;
    }

    setIsVerifying(true);
    setAffiliateCodeError(null);
    
    try {
      const result = await verifyAffiliateCode(extractedCode);
      
      setAffiliateCodeValid(result.isValid);
      setAffiliateCodeError(result.error || null);
      
      if (result.isValid) {
        localStorage.setItem("deliva_affiliate_code", extractedCode);
        setAffiliateCode(extractedCode);
        setAffiliateCodeLocked(true);
      }
      // Don't remove from localStorage on failure — preserves previously verified code
    } catch (error) {
      console.error("Error verifying affiliate code:", error);
      setAffiliateCodeValid(false);
      setAffiliateCodeError("Failed to verify affiliate code");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleAffiliateCodeChange = (e) => {
    if (affiliateCodeLocked) {
      return;
    }
    userChangedCode.current = true;
    const value = e.target.value;
    setAffiliateCode(value);
  };

  return {
    affiliateCode,
    affiliateCodeValid,
    affiliateCodeLocked,
    affiliateCodeError,
    isVerifying,
    handleAffiliateCodeChange,
    verifyAffiliateCode: handleVerification,
  };
};
